/**
 * Flowteras Timezone Utilities
 * 
 * IMPROVEMENT 5: Frontend Timezone Display
 * Converts UTC timestamps to organization's local timezone for display.
 * 
 * All timestamps remain stored in UTC in the database.
 * Frontend converts using organization.ai_config.default_timezone.
 */

// Default timezone if none is set - Asia/Kuala_Lumpur as fallback
const DEFAULT_TIMEZONE = 'Asia/Kuala_Lumpur';

/**
 * Convert a UTC timestamp to the organization's local timezone.
 * 
 * @param {string|Date} utcTimestamp - UTC timestamp (ISO string or Date object)
 * @param {string} timezone - IANA timezone string (e.g., 'Asia/Kuala_Lumpur')
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted local time string
 */
export function formatToLocalTime(utcTimestamp, timezone = DEFAULT_TIMEZONE, options = {}) {
  if (!utcTimestamp) return '';
  
  try {
    const date = typeof utcTimestamp === 'string' 
      ? new Date(utcTimestamp) 
      : utcTimestamp;
    
    if (isNaN(date.getTime())) return '';
    
    const defaultOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...options
    };
    
    return new Intl.DateTimeFormat('en-US', {
      ...defaultOptions,
      timeZone: timezone
    }).format(date);
  } catch (error) {
    console.warn('Timezone conversion error:', error);
    // Fallback to UTC if timezone is invalid
    return new Date(utcTimestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
}

/**
 * Format a UTC timestamp to local date and time.
 * 
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted date and time (e.g., "Mar 8, 9:34 PM")
 */
export function formatToLocalDateTime(utcTimestamp, timezone = DEFAULT_TIMEZONE) {
  if (!utcTimestamp) return '';
  
  try {
    const date = typeof utcTimestamp === 'string' 
      ? new Date(utcTimestamp) 
      : utcTimestamp;
    
    if (isNaN(date.getTime())) return '';
    
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone
    }).format(date);
  } catch (error) {
    console.warn('Timezone conversion error:', error);
    return new Date(utcTimestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
}

/**
 * Format a UTC timestamp to local date only.
 * 
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted date (e.g., "Mar 8, 2026")
 */
export function formatToLocalDate(utcTimestamp, timezone = DEFAULT_TIMEZONE) {
  if (!utcTimestamp) return '';
  
  try {
    const date = typeof utcTimestamp === 'string' 
      ? new Date(utcTimestamp) 
      : utcTimestamp;
    
    if (isNaN(date.getTime())) return '';
    
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: timezone
    }).format(date);
  } catch (error) {
    console.warn('Timezone conversion error:', error);
    return new Date(utcTimestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }
}

/**
 * Get relative time string (e.g., "2 hours ago", "just now").
 * Uses organization timezone for accurate local day comparison.
 * 
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @param {string} timezone - IANA timezone string
 * @returns {string} Relative time string
 */
export function getRelativeTime(utcTimestamp, timezone = DEFAULT_TIMEZONE) {
  if (!utcTimestamp) return '';
  
  try {
    const date = typeof utcTimestamp === 'string' 
      ? new Date(utcTimestamp) 
      : utcTimestamp;
    
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay === 1) return 'yesterday';
    if (diffDay < 7) return `${diffDay}d ago`;
    
    // For older dates, show the local date
    return formatToLocalDate(date, timezone);
  } catch (error) {
    console.warn('Relative time error:', error);
    return '';
  }
}

/**
 * Format timestamp for inbox conversation list.
 * Shows time for today, date for older.
 * 
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted timestamp for inbox display
 */
export function formatInboxTimestamp(utcTimestamp, timezone = DEFAULT_TIMEZONE) {
  if (!utcTimestamp) return '';
  
  try {
    const date = typeof utcTimestamp === 'string' 
      ? new Date(utcTimestamp) 
      : utcTimestamp;
    
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    
    // Check if same day (in local timezone)
    const dateLocal = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone
    }).format(date);
    
    const nowLocal = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone
    }).format(now);
    
    if (dateLocal === nowLocal) {
      // Today - show time only
      return formatToLocalTime(date, timezone);
    }
    
    // Check if yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayLocal = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone
    }).format(yesterday);
    
    if (dateLocal === yesterdayLocal) {
      return 'Yesterday';
    }
    
    // Older - show short date
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: timezone
    }).format(date);
  } catch (error) {
    console.warn('Inbox timestamp error:', error);
    return '';
  }
}

/**
 * Format timestamp for message display in conversation.
 * 
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted message timestamp
 */
export function formatMessageTimestamp(utcTimestamp, timezone = DEFAULT_TIMEZONE) {
  if (!utcTimestamp) return '';
  
  try {
    const date = typeof utcTimestamp === 'string' 
      ? new Date(utcTimestamp) 
      : utcTimestamp;
    
    if (isNaN(date.getTime())) return '';
    
    // PART 2 FIX: Use en-MY locale for Malaysia timezone formatting
    const locale = timezone.includes('Asia/Kuala_Lumpur') || timezone.includes('Malaysia') 
      ? 'en-MY' 
      : 'en-US';
    
    return new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone
    }).format(date);
  } catch (error) {
    console.warn('Message timestamp error:', error);
    return new Date(utcTimestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
}

/**
 * Get the current time in a specific timezone.
 * 
 * @param {string} timezone - IANA timezone string
 * @returns {Date} Current time adjusted for display
 */
export function getCurrentTimeInTimezone(timezone = DEFAULT_TIMEZONE) {
  try {
    // Get formatted time string in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    return formatter.format(new Date());
  } catch (error) {
    console.warn('Current time error:', error);
    return new Date().toLocaleString();
  }
}

export default {
  formatToLocalTime,
  formatToLocalDateTime,
  formatToLocalDate,
  getRelativeTime,
  formatInboxTimestamp,
  formatMessageTimestamp,
  getCurrentTimeInTimezone
};
